// ---------------------------------------------------------------------------
// fetch patch — CSRF protection + session-expiry redirect
//
// Wraps window.fetch to:
//   1. Automatically attach credentials and the Drupal CSRF token to mutating
//      requests (POST/PUT/PATCH/DELETE) targeting Drupal API routes.
//   2. Redirect to /user/login on 401 responses (session expired).
//      403 ("authenticated but forbidden") is left to the calling component.
//
// Must be installed BEFORE installRequestBatcher() and installRequestCache()
// so those layers wrap this one, not the other way around.
// ---------------------------------------------------------------------------

import { ensureCsrfToken, _nativeFetch, MUTATING_METHODS, getPathname, isDrupalApiUrl } from './csrf.js';
import { DRUPAL_BASE_URL } from '../config/drupal.js';

let publicRenderMode = false;

/**
 * Coupe la redirection login du 401 pendant le rendu public d'une page builder.
 *
 * Sur une page publiée, le visiteur est anonyme par construction : le renvoyer
 * vers /user/login serait faux (il n'a rien à s'authentifier) et destructeur
 * (il perdrait la page qu'il consultait). Le 401 remonte alors à l'appelant,
 * qui l'affiche comme n'importe quelle erreur de chargement.
 */
export function setPublicRenderMode(enabled) {
  publicRenderMode = !!enabled;
}

export function installFetchPatch() {
  window.fetch = async function csrfFetch(input, init) {
    const rawUrl = typeof input === 'string' ? input : (input instanceof Request ? input.url : null);
    const drupalRoute = isDrupalApiUrl(rawUrl);
    const pathname = getPathname(rawUrl);

    if (drupalRoute) {
      if (typeof input === 'string') {
        if (!init) init = {};
        init.credentials = 'include';
        const method = (init.method || 'GET').toUpperCase();
        if (MUTATING_METHODS.includes(method)) {
          const token = await ensureCsrfToken();
          if (token) {
            const h = new Headers(init.headers || {});
            if (!h.has('X-CSRF-Token')) h.set('X-CSRF-Token', token);
            init.headers = h;
          }
        }
      } else if (input instanceof Request) {
        const method = input.method.toUpperCase();
        if (MUTATING_METHODS.includes(method)) {
          const token = await ensureCsrfToken();
          if (token && !input.headers.has('X-CSRF-Token')) {
            const newHeaders = new Headers(input.headers);
            newHeaders.set('X-CSRF-Token', token);
            input = new Request(input, { headers: newHeaders, credentials: 'include' });
          }
        }
      }
    }

    const response = await _nativeFetch(input, init);

    if (
      drupalRoute &&
      !publicRenderMode &&
      !pathname.startsWith('/session/') &&
      response.status === 401
    ) {
      const loginUrl = `${DRUPAL_BASE_URL}/user/login`;
      if (!window.__authRedirecting && !window.location.href.startsWith(loginUrl)) {
        window.__authRedirecting = true;
        try {
          const returnPath = window.location.pathname + window.location.search + window.location.hash;
          if (returnPath) sessionStorage.setItem('orbit_auth_return', returnPath);
        } catch (_) {}
        window.location.href = loginUrl;
      }
    }

    return response;
  };
}
